import React, { useState, useEffect, createContext, useContext } from 'react';
import Icon from '../AppIcon';

// Toast Context
const ToastContext = createContext();

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};

// Toast Provider
export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const addToast = (message, type = 'success', duration = 3000) => {
    const id = Date.now() + Math.random();
    const newToast = { id, message, type, duration };
    
    setToasts(prev => [...prev, newToast]);

    // Auto remove after duration
    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }

    return id;
  };

  const removeToast = (id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  };

  const toast = {
    success: (message, duration) => addToast(message, 'success', duration),
    error: (message, duration) => addToast(message, 'error', duration),
    warning: (message, duration) => addToast(message, 'warning', duration),
    info: (message, duration) => addToast(message, 'info', duration),
  };

  return (
    <ToastContext.Provider value={toast}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
};

// Toast Container
const ToastContainer = ({ toasts, onRemove }) => {
  return (
    <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-2 pointer-events-none">
      {toasts.map(toast => (
        <Toast key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
};

// Individual Toast Component
const Toast = ({ toast, onRemove }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);

  useEffect(() => {
    // Trigger enter animation
    setTimeout(() => setIsVisible(true), 10);
    
    // Progress bar animation
    if (toast.duration > 0) {
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, 100 - (elapsed / toast.duration) * 100);
        setProgress(remaining);
        
        if (remaining === 0) {
          clearInterval(interval);
        }
      }, 50);
      
      return () => clearInterval(interval);
    }
  }, [toast.duration]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => onRemove(toast.id), 300);
  };

  const getToastStyles = () => {
    switch (toast.type) {
      case 'success':
        return {
          bg: 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30',
          border: 'border-green-300 dark:border-green-700',
          text: 'text-green-900 dark:text-green-100',
          icon: 'CheckCircle',
          iconColor: 'text-green-600 dark:text-green-400',
          iconBg: 'bg-green-100 dark:bg-green-800/50',
          progressBg: 'bg-green-500 dark:bg-green-400',
          glow: 'shadow-green-500/20'
        };
      case 'error':
        return {
          bg: 'bg-gradient-to-r from-red-50 to-rose-50 dark:from-red-900/30 dark:to-rose-900/30',
          border: 'border-red-300 dark:border-red-700',
          text: 'text-red-900 dark:text-red-100',
          icon: 'XCircle',
          iconColor: 'text-red-600 dark:text-red-400',
          iconBg: 'bg-red-100 dark:bg-red-800/50',
          progressBg: 'bg-red-500 dark:bg-red-400',
          glow: 'shadow-red-500/20'
        };
      case 'warning':
        return {
          bg: 'bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30',
          border: 'border-amber-300 dark:border-amber-700',
          text: 'text-amber-900 dark:text-amber-100',
          icon: 'AlertTriangle',
          iconColor: 'text-amber-600 dark:text-amber-400',
          iconBg: 'bg-amber-100 dark:bg-amber-800/50',
          progressBg: 'bg-amber-500 dark:bg-amber-400',
          glow: 'shadow-amber-500/20'
        };
      case 'info':
        return {
          bg: 'bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/30 dark:to-cyan-900/30',
          border: 'border-blue-300 dark:border-blue-700',
          text: 'text-blue-900 dark:text-blue-100',
          icon: 'Info',
          iconColor: 'text-blue-600 dark:text-blue-400',
          iconBg: 'bg-blue-100 dark:bg-blue-800/50',
          progressBg: 'bg-blue-500 dark:bg-blue-400',
          glow: 'shadow-blue-500/20'
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-gray-50 to-slate-50 dark:from-gray-900/30 dark:to-slate-900/30',
          border: 'border-gray-300 dark:border-gray-700',
          text: 'text-gray-900 dark:text-gray-100',
          icon: 'Info',
          iconColor: 'text-gray-600 dark:text-gray-400',
          iconBg: 'bg-gray-100 dark:bg-gray-800/50',
          progressBg: 'bg-gray-500 dark:bg-gray-400',
          glow: 'shadow-gray-500/20'
        };
    }
  };

  const styles = getToastStyles();

  return (
    <div
      className={`
        ${styles.bg} ${styles.border} ${styles.text}
        border-2 rounded-xl shadow-2xl ${styles.glow}
        p-4 min-w-[320px] max-w-md
        pointer-events-auto relative overflow-hidden
        transform transition-all duration-300 ease-out
        ${isVisible ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'}
        backdrop-blur-sm
      `}
    >
      {/* Progress Bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200/30 dark:bg-gray-700/30">
        <div
          className={`h-full ${styles.progressBg} transition-all duration-100 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="flex items-start gap-3">
        {/* Icon with background circle */}
        <div className={`${styles.iconBg} rounded-full p-2 flex-shrink-0 animate-pulse`}>
          <Icon name={styles.icon} size={20} className={styles.iconColor} />
        </div>
        
        <div className="flex-1 min-w-0 pt-1">
          <p className="text-sm font-semibold break-words leading-relaxed whitespace-pre-line">
            {toast.message}
          </p>
        </div>
        
        <button
          onClick={handleClose}
          className={`${styles.iconColor} hover:opacity-70 transition-all hover:scale-110 flex-shrink-0 mt-1`}
          aria-label="Close"
        >
          <Icon name="X" size={18} />
        </button>
      </div>
    </div>
  );
};

export default Toast;

